import axios, { type AxiosError, type Method } from 'axios';

import { useUiStore } from '@/stores/ui-store';

import { ApiError } from '@/lib/api/api-error';
import { recoverFrom401 } from '@/lib/api/session-guard';

export interface ApiRequestOptions {
    url: string;
    method?: Method;
    data?: unknown;
    /**
     * `Content-Type` envoyé. Les routes d'authentification attendent du `x-www-form-urlencoded`
     * par défaut — c'est axios qui sérialise alors l'objet passé en `data`.
     */
    header?: string;
}

/** Erreur d'une route d'authentification, telle que la renvoie axios (`error.response.data.message`…). */
export type AuthApiError = AxiosError<{ message?: string; errors?: Record<string, string[]> } & Record<string, unknown>>;

/**
 * Message métier renvoyé par le Pass dans le corps d'une erreur, ou chaîne vide.
 *
 * Lit les deux formes : l'erreur v2 normalisée (`ApiError`, détail de validation en priorité) et
 * l'erreur axios brute des routes d'authentification (`response.data.message`).
 */
export const getApiErrorMessage = (error: unknown): string => {
    if (error instanceof ApiError) return error.validationMessage ?? error.message;
    return (axios.isAxiosError(error) && (error.response?.data as { message?: string } | undefined)?.message) || '';
};

const send = <T>({ url, method = 'GET', data, header = 'application/x-www-form-urlencoded' }: ApiRequestOptions) =>
    axios<T>({
        method,
        url,
        data,
        headers: { 'content-type': header },
        // Les jetons (PASS ou Gravitee AM) sont dans des cookies HttpOnly : pas de Bearer à injecter.
        withCredentials: true,
    }).then((response) => response.data);

/**
 * Appel aux routes d'authentification du Pass (`/oauth/token`, `/auth/*`), servies à la racine
 * de son domaine, hors de l'API versionnée et de son enveloppe. Tout le reste passe par
 * `client-v2`.
 *
 * - **401** : en MFA, on tente un rafraîchissement du jeton Gravitee (un seul à la fois, les
 *   appels concurrents attendent son issue) puis on rejoue la requête ; sinon, ou si le
 *   rafraîchissement échoue, la session est fermée.
 * - **503** : l'application bascule en maintenance.
 *
 * Dans tous les cas d'échec, la promesse est rejetée avec l'erreur axios d'origine — c'est elle
 * que lisent les écrans pour afficher `response.data.message`.
 */
export const apiRequest = <T = unknown>(options: ApiRequestOptions): Promise<T> =>
    send<T>(options).catch((error: unknown) => {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;

        if (status === 401) {
            return recoverFrom401(() => send<T>(options), error);
        }

        if (status === 503) {
            useUiStore.getState().setMaintenance(true);
        }

        throw error;
    });
